

import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../types';
import JSZip from 'jszip';
// FIX: Import 'X' icon from lucide-react.
import { 
    ImageIcon, Trash2, Upload, Loader2, Link as LinkIcon, 
    Maximize2, Check, CheckSquare, Square, SlidersHorizontal, X, XCircle, CheckCircle, RefreshCw, Download
} from 'lucide-react';

interface MediaItem {
    id: number;
    url: string;
    url_webp?: string;
    type: number;
    size?: number;
}

interface Props {
    currentUser: UserProfile;
    mediaList: MediaItem[];
    onDataNeededRefresh: () => void;
    setPreviewMedia: (item: MediaItem | null) => void;
}

const formatBytes = (bytes?: number) => {
    if (!bytes && bytes !== 0) return '...';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const CmsMediaItem: React.FC<{ 
    item: MediaItem; 
    selected: boolean;
    onSelect: (e: React.MouseEvent) => void;
    onPreview: (e: React.MouseEvent) => void;
}> = ({ item, selected, onSelect, onPreview }) => {
    const [dimensions, setDimensions] = useState<string>('...');
    const [isCopied, setIsCopied] = useState(false);

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setDimensions(`${img.naturalWidth}x${img.naturalHeight}`);
    };

    const copyLink = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(item.url);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };
    
    // Add a cache-busting parameter. Use size, as it changes on optimization. Fallback to ID.
    const imageUrl = `${item.url}?v=${item.size || item.id}`;

    return (
        <div 
            onClick={onSelect}
            className={`
                group relative bg-mystic-inset rounded border cursor-pointer overflow-hidden flex flex-col h-full transition-all
                ${selected ? 'border-mystic-accent ring-1 ring-mystic-accent' : 'border-gray-800 hover:border-gray-600'}
            `}
        >
            <div className="relative aspect-square w-full bg-mystic-inset overflow-hidden">
                <img 
                    src={imageUrl} 
                    alt={`Media ${item.id}`}
                    onLoad={handleImageLoad}
                    className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" 
                />
                
                <div className="absolute top-2 left-2 z-10">
                     {selected ? (
                        <CheckSquare className="w-5 h-5 text-mystic-accent bg-black rounded-sm" />
                     ) : (
                        <Square className="w-5 h-5 text-gray-500 bg-black/50 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                     )}
                </div>

                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                     <button 
                        onClick={copyLink}
                        className="p-2 bg-white text-black rounded-full hover:bg-mystic-accent transition-colors"
                        title="Copy Link"
                     >
                        {isCopied ? <Check className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
                     </button>
                     <button 
                        onClick={onPreview}
                        className="p-2 bg-white text-black rounded-full hover:bg-mystic-accent transition-colors" 
                        title="View"
                     >
                        <Maximize2 className="w-4 h-4" />
                     </button>
                </div>
            </div>

            <div className="p-2 bg-mystic-inset border-t border-gray-800 text-[10px] text-gray-400 font-mono flex justify-between items-center mt-auto">
                <span>{dimensions}</span>
                <span className={item.size && item.size > 200 * 1024 ? 'text-red-500 font-bold' : 'text-gray-500'}>
                    {formatBytes(item.size)}
                </span>
            </div>
        </div>
    );
};

interface ProgressItem {
  id: number;
  url: string;
  originalSize: number;
  newSize: number | null;
  status: 'pending' | 'optimizing' | 'done' | 'error';
  error?: string;
}

const OptimizeModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    mediaList: MediaItem[];
    selectedIds: Set<number | string>;
    currentUser: UserProfile;
}> = ({ isOpen, onClose, mediaList, selectedIds, currentUser }) => {
    const [quality, setQuality] = useState(75);
    const [processState, setProcessState] = useState<'idle' | 'processing' | 'done'>('idle');
    const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);

    useEffect(() => {
        if (isOpen) {
            const itemsToProcess = mediaList
                .filter(item => selectedIds.has(item.id) && item.url.startsWith('https://'))
                .map(item => ({
                    id: item.id,
                    url: item.url,
                    originalSize: item.size || 0,
                    newSize: null,
                    status: 'pending' as 'pending',
                }));
            setProgressItems(itemsToProcess);
            setProcessState('idle');
            setQuality(75);
        }
    }, [isOpen, selectedIds, mediaList]);

    const handleStartOptimization = async () => {
        setProcessState('processing');

        for (const item of progressItems) {
            setProgressItems(prev => prev.map(p => p.id === item.id ? { ...p, status: 'optimizing' } : p));
            
            try {
                const img = new Image();
                img.crossOrigin = "anonymous";
                const url = new URL(item.url);
                url.searchParams.set('t', Date.now().toString());
                img.src = url.toString();

                await new Promise<void>((resolve, reject) => {
                    img.onload = () => resolve();
                    img.onerror = () => reject(new Error(`CORS error loading image.`));
                });
        
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error("Canvas context failed");
                ctx.drawImage(img, 0, 0);
        
                const newDataUrl = canvas.toDataURL('image/jpeg', quality / 100);

                const res = await fetch('/api/cms/media', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                    body: JSON.stringify({ id: item.id, url: newDataUrl })
                });

                if (!res.ok) {
                     const err = await res.json();
                     throw new Error(err.error || "Server error");
                }
                const data = await res.json();
                setProgressItems(prev => prev.map(p => p.id === item.id ? { ...p, status: 'done', newSize: data.size } : p));
                
            } catch (e: any) {
                 setProgressItems(prev => prev.map(p => p.id === item.id ? { ...p, status: 'error', error: e.message } : p));
            }
        }
        setProcessState('done');
    };
    
    if (!isOpen) return null;

    return (
         <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-mystic-box border border-gray-700 w-full max-w-2xl rounded-xl shadow-2xl flex flex-col relative max-h-[90vh]">
                <header className="p-4 border-b border-gray-800 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <SlidersHorizontal className="w-5 h-5 text-yellow-400"/> Bulk Optimize Images
                    </h3>
                    <button onClick={onClose} disabled={processState === 'processing'} className="disabled:opacity-50"><X className="w-5 h-5 text-gray-500 hover:text-white"/></button>
                </header>
                
                <div className="p-6 flex-1 overflow-y-auto">
                    {processState === 'idle' && (
                        <div className="animate-fade-in space-y-6">
                            <p className="text-gray-400 text-sm">You are about to optimize <span className="font-bold text-white">{progressItems.length}</span> images. This will overwrite the original files. This action cannot be undone.</p>
                             <div className="p-4 bg-black/20 rounded-xl border border-gray-800">
                                <label className="block text-sm text-gray-300 mb-2 font-bold">JPEG Quality: {quality}%</label>
                                <input 
                                    type="range" 
                                    min="10" 
                                    max="100" 
                                    value={quality}
                                    onChange={(e) => setQuality(parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-mystic-accent"
                                />
                                <p className="text-xs text-gray-500 mt-2">Recommended: 75-85%. Lower values mean smaller files but lower quality.</p>
                            </div>
                        </div>
                    )}

                    {(processState === 'processing' || processState === 'done') && (
                        <div className="space-y-2">
                            {progressItems.map(item => {
                                // Add cache-busting param to the URL if the image is processed
                                const imageUrl = item.newSize ? `${item.url}?v=${item.newSize}` : item.url;
                                return (
                                <div key={item.id} className="grid grid-cols-12 gap-4 items-center bg-mystic-bg p-2 rounded-md border border-gray-800">
                                    <div className="col-span-1">
                                        <img src={imageUrl} className="w-10 h-10 rounded object-cover bg-black" alt="" />
                                    </div>
                                    <div className="col-span-5 text-xs text-gray-300 truncate font-mono">
                                        ID #{item.id}
                                    </div>
                                     <div className="col-span-2 text-xs text-gray-500 text-right">
                                        {formatBytes(item.originalSize)}
                                    </div>
                                    <div className="col-span-2 text-xs text-right font-bold">
                                        {item.status === 'done' && <span className="text-green-400">{formatBytes(item.newSize)}</span>}
                                        {item.status === 'error' && <span className="text-red-400">Error</span>}
                                    </div>
                                    <div className="col-span-2 flex justify-center">
                                        {item.status === 'pending' && <span className="text-gray-600 text-xs">Pending</span>}
                                        {item.status === 'optimizing' && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                                        {item.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                        {/* FIX: Lucide icons do not have a 'title' prop. Wrap in a span with a title attribute. */}
                                        {item.status === 'error' && <span title={item.error}><XCircle className="w-4 h-4 text-red-500" /></span>}
                                    </div>
                                </div>
                            )})}
                        </div>
                    )}
                </div>

                <footer className="p-4 border-t border-gray-800">
                     {processState === 'idle' && (
                        <button onClick={handleStartOptimization} className="w-full bg-yellow-600 hover:bg-yellow-500 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                            <RefreshCw className="w-4 h-4"/> Start Optimization
                        </button>
                     )}
                     {processState === 'processing' && (
                         <button disabled className="w-full bg-gray-600 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 cursor-not-allowed">
                            <Loader2 className="animate-spin w-4 h-4"/> Processing...
                        </button>
                     )}
                      {processState === 'done' && (
                         <button onClick={onClose} className="w-full bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                            Close
                        </button>
                     )}
                </footer>
            </div>
        </div>
    );
};

interface DownloadProgress {
    current: number;
    total: number;
    status: 'idle' | 'fetching' | 'zipping' | 'done' | 'error';
    error?: string;
}

const DownloadProgressModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    progress: DownloadProgress;
}> = ({ isOpen, onClose, progress }) => {
    if (!isOpen) return null;

    const progressPercentage = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;
    
    let statusText = "Przygotowywanie...";
    if (progress.status === 'fetching') statusText = `Pobieranie pliku ${progress.current} z ${progress.total}...`;
    if (progress.status === 'zipping') statusText = "Tworzenie archiwum ZIP...";
    if (progress.status === 'done') statusText = "Pobieranie zakończone!";
    if (progress.status === 'error') statusText = `Błąd: ${progress.error}`;

    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-mystic-box border border-gray-700 w-full max-w-md rounded-xl shadow-2xl flex flex-col relative">
                <header className="p-4 border-b border-gray-800 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Download className="w-5 h-5 text-green-400"/> Pobieranie plików
                    </h3>
                    <button onClick={onClose} disabled={progress.status !== 'done' && progress.status !== 'error'} className="disabled:opacity-50"><X className="w-5 h-5 text-gray-500 hover:text-white"/></button>
                </header>
                <div className="p-6">
                    <div className="w-full bg-gray-800 rounded-full h-2.5 mb-2">
                        <div className="bg-green-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progressPercentage}%` }}></div>
                    </div>
                    <div className="text-center text-sm text-gray-400">
                        {statusText}
                    </div>
                     {progress.status === 'done' && (
                        <button onClick={onClose} className="mt-6 w-full bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-3 rounded-lg">Zamknij</button>
                     )}
                     {progress.status === 'error' && (
                        <button onClick={onClose} className="mt-6 w-full bg-red-800 hover:bg-red-700 text-white font-bold py-3 rounded-lg">Zamknij</button>
                     )}
                </div>
            </div>
        </div>
    );
};


const MediaTab: React.FC<Props> = ({ currentUser, mediaList, onDataNeededRefresh, setPreviewMedia }) => {
    const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
    const [lastSelectedId, setLastSelectedId] = useState<number | string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isOptimizeModalOpen, setIsOptimizeModalOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({ current: 0, total: 0, status: 'idle' });
    
    useEffect(() => {
        setSelectedIds(new Set());
    }, [mediaList]);

    const handleSelectAll = () => {
        if (mediaList.length > 0 && selectedIds.size === mediaList.length) {
            setSelectedIds(new Set());
        } else {
            const allIds = new Set(mediaList.map(item => item.id).filter(Boolean));
            setSelectedIds(allIds);
        }
    };

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newSelected = new Set(selectedIds);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = mediaList.findIndex(m => m.id === id);
            const lastIndex = mediaList.findIndex(m => m.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            for (let i = start; i <= end; i++) {
                if(mediaList[i].id) newSelected.add(mediaList[i].id!);
            }
        } else {
            if (newSelected.has(id)) newSelected.delete(id);
            else newSelected.add(id);
        }

        setSelectedIds(newSelected);
        setLastSelectedId(id);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0 || !currentUser) return;
      
      setIsUploading(true);
      const uploadPromises = Array.from(files).map((file: File) => {
          return new Promise<void>(async (resolve) => {
              const originalDataUrl = await new Promise<string>(r => {
                  const reader = new FileReader();
                  reader.onload = () => r(reader.result as string);
                  reader.readAsDataURL(file);
              });

              try {
                  const res = await fetch('/api/cms/media', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                      body: JSON.stringify({
                          url: originalDataUrl,
                          type: 1,
                          original_filename: file.name
                      })
                  });
                  if (!res.ok) alert(`Upload Failed: ${(await res.json()).error}`);
              } catch (e) { console.error(e); }
              resolve();
          });
      });

      await Promise.all(uploadPromises);
      setIsUploading(false);
      e.target.value = ''; 
      onDataNeededRefresh();
    };

    const handleBulkDeleteMedia = async () => {
        if (selectedIds.size === 0) return;
        if (!confirm(`Permanently delete ${selectedIds.size} media files? This cannot be undone.`)) return;

        try {
            const res = await fetch('/api/cms/media', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: Array.from(selectedIds) })
            });
            if (res.ok) {
                onDataNeededRefresh();
                setSelectedIds(new Set());
            } else {
                alert('Delete failed');
            }
        } catch (e) { console.error(e); }
    };
    
    const handleBulkDownload = async () => {
        if (selectedIds.size === 0) return;
        const selectedItems = mediaList.filter(item => selectedIds.has(item.id));
    
        setIsDownloadModalOpen(true);
        setDownloadProgress({ current: 0, total: selectedItems.length, status: 'idle' });
    
        const zip = new JSZip();
    
        try {
            for (const [index, item] of selectedItems.entries()) {
                setDownloadProgress(prev => ({ ...prev, current: index + 1, status: 'fetching' }));
                try {
                    const urlToFetch = `${item.url}?t=${Date.now()}`;
                    const response = await fetch(`/api/download?url=${encodeURIComponent(urlToFetch)}`);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch ${item.url} (status: ${response.status})`);
                    }
                    const blob = await response.blob();
                    const filename = item.url.substring(item.url.lastIndexOf('/') + 1) || `media-${item.id}.jpg`;
                    zip.file(filename, blob);
                } catch (error: any) {
                    console.error(`Error downloading file ${item.url}:`, error);
                    zip.file(`_error_fetching_${item.url.substring(item.url.lastIndexOf('/') + 1)}.txt`, error.message);
                }
            }
    
            setDownloadProgress(prev => ({ ...prev, status: 'zipping' }));
    
            const content = await zip.generateAsync({ type: "blob" });
            
            const link = document.createElement('a');
            link.href = URL.createObjectURL(content);
            link.download = `esoteric-media-export-${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
    
            setDownloadProgress(prev => ({ ...prev, status: 'done' }));
    
        } catch (error: any) {
            setDownloadProgress({ current: selectedItems.length, total: selectedItems.length, status: 'error', error: error.message });
            console.error("Error creating zip file:", error);
        }
    };

    return (
        <div className="w-full relative animate-fade-in">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <ImageIcon className="w-5 h-5" /> Media Manager
                    </h2>
                    {selectedIds.size > 0 && (
                        <div className="flex items-center gap-2 animate-fade-in bg-blue-900/30 border border-blue-700/50 rounded h-8 px-2 py-1">
                            <span className="text-xs text-blue-200">{selectedIds.size} selected</span>
                            <div className="h-4 w-px bg-blue-700/50"></div>
                            <button onClick={handleBulkDownload} className="text-green-400 hover:text-green-300 p-1" title="Download Selected">
                                <Download className="w-4 h-4" />
                            </button>
                            <button onClick={() => setIsOptimizeModalOpen(true)} className="text-yellow-400 hover:text-yellow-300 p-1" title="Optimize Selected">
                                <SlidersHorizontal className="w-4 h-4" />
                            </button>
                            <button onClick={handleBulkDeleteMedia} className="text-red-400 hover:text-red-300 p-1" title="Delete Selected">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center gap-3">
                     <div className="flex items-center gap-2 text-gray-400 text-xs mr-4 cursor-pointer hover:text-white" onClick={handleSelectAll}>
                        {mediaList.length > 0 && selectedIds.size === mediaList.length ? (
                            <><CheckSquare className="w-4 h-4 text-blue-400" /> Deselect All</>
                        ) : (
                            <><Square className="w-4 h-4" /> Select All</>
                        )}
                     </div>

                     <label className={`bg-mystic-accent hover:bg-mystic-accentHover text-black px-4 py-1.5 rounded cursor-pointer flex items-center gap-2 text-xs font-bold transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Upload New
                        <input type="file" accept="image/*" multiple className="hidden" onChange={handleFileUpload} disabled={isUploading} />
                    </label>
                </div>
            </div>
            
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {mediaList.map(item => (
                    <CmsMediaItem 
                        key={item.id} 
                        item={item} 
                        selected={selectedIds.has(item.id)}
                        onSelect={(e) => handleSelectRow(item.id, e)}
                        onPreview={(e) => { e.stopPropagation(); setPreviewMedia(item); }}
                    />
                ))}
                {mediaList.length === 0 && (
                    <div className="col-span-full py-20 text-center text-gray-500 border-2 border-dashed border-gray-800 rounded-xl">
                        No media files found.
                    </div>
                )}
            </div>

            <OptimizeModal
                isOpen={isOptimizeModalOpen}
                onClose={() => {
                    setIsOptimizeModalOpen(false);
                    onDataNeededRefresh();
                }}
                mediaList={mediaList}
                selectedIds={selectedIds}
                currentUser={currentUser}
            />

            <DownloadProgressModal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                progress={downloadProgress}
            />
        </div>
    );
};

export default MediaTab;